import React, { useMemo } from 'react';
import { Badge, Card, Table, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CompetencyScore, JohariQuadrant } from '../../types';

interface GapTableProps {
  scores: CompetencyScore[];
}

const QUADRANT_COLORS: Record<JohariQuadrant, string> = {
  open: 'teal',
  blind_spot: 'red',
  hidden_strength: 'blue',
  unknown: 'gray',
};

const QUADRANT_I18N: Record<JohariQuadrant, string> = {
  open: 'review360.johari.open',
  blind_spot: 'review360.johari.blindSpot',
  hidden_strength: 'review360.johari.hiddenStrength',
  unknown: 'review360.johari.unknown',
};

export const GapTable: React.FC<GapTableProps> = ({ scores }) => {
  const { t } = useTranslation();

  const sorted = useMemo(
    () =>
      [...scores].filter((s) => s.gap !== null).sort((a, b) => Math.abs(b.gap!) - Math.abs(a.gap!)),
    [scores],
  );

  const getGapColor = (gap: number): string => {
    if (Math.abs(gap) < 0.3) return 'gray';
    return gap > 0 ? 'red' : 'teal';
  };

  const formatGap = (gap: number): string => {
    const sign = gap > 0 ? '+' : '';
    return `${sign}${gap.toFixed(1)}`;
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Title order={4} mb="md">
        {t('review360.gapAnalysis')}
      </Title>
      <Table.ScrollContainer minWidth={400}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('review360.competency')}</Table.Th>
              <Table.Th>{t('review360.self')}</Table.Th>
              <Table.Th>{t('review360.others')}</Table.Th>
              <Table.Th>{t('review360.gap')}</Table.Th>
              <Table.Th>{t('review360.quadrant')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((score) => (
              <Table.Tr key={score.sectionId}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {score.sectionTitle}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {score.selfScore !== null ? score.selfScore.toFixed(1) : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {score.overallAvg !== null ? score.overallAvg.toFixed(1) : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600} c={getGapColor(score.gap!)}>
                    {formatGap(score.gap!)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {score.quadrant ? (
                    <Badge color={QUADRANT_COLORS[score.quadrant]} variant="light" size="sm">
                      {t(QUADRANT_I18N[score.quadrant])}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
};
